import { z } from "zod";

/**
 * Melhorias sugeridas em segundo plano: enquanto a pessoa programa, a IA revisa o arquivo
 * aberto e propõe mudanças concretas (com as linhas exatas e o código novo). Este é o contrato
 * entre o serviço de IA e o editor.
 */
export const IMPROVEMENT_KINDS = [
  "bug",
  "seguranca",
  "desempenho",
  "legibilidade",
  "teste",
  "proximo-passo",
] as const;
export type ImprovementKind = (typeof IMPROVEMENT_KINDS)[number];

export const IMPROVEMENT_KIND_LABELS: Record<ImprovementKind, string> = {
  bug: "Bug",
  seguranca: "Segurança",
  desempenho: "Desempenho",
  legibilidade: "Legibilidade",
  teste: "Teste",
  "proximo-passo": "Próximo passo",
};

export const IMPROVEMENT_LIMITS = {
  maxFileChars: 60_000,
  maxSuggestions: 5,
  maxCodeChars: 8_000,
} as const;

export const AiImproveInput = z.object({
  projectId: z.uuid(),
  path: z.string().min(1).max(500),
  language: z.string().max(40).optional(),
  content: z.string().min(1).max(IMPROVEMENT_LIMITS.maxFileChars),
  /** Problemas que o editor já encontrou (lint e sintaxe), para a IA priorizar. */
  diagnostics: z
    .array(
      z.object({
        line: z.number().int().min(1),
        severity: z.enum(["error", "warning", "info", "hint"]),
        message: z.string().max(300),
      }),
    )
    .max(30)
    .optional(),
});
export type AiImproveInput = z.infer<typeof AiImproveInput>;

export const ImprovementSuggestion = z.object({
  id: z.string(),
  kind: z.enum(IMPROVEMENT_KINDS),
  severity: z.enum(["alta", "media", "baixa"]),
  title: z.string().min(1).max(120),
  explanation: z.string().max(600),
  /** replace: troca as linhas startLine..endLine; insert_after: insere depois de endLine. */
  action: z.enum(["replace", "insert_after"]),
  startLine: z.number().int().min(1),
  endLine: z.number().int().min(1),
  /** Texto exato das linhas no momento da análise: se mudou, a sugestão ficou velha. */
  original: z.string().max(IMPROVEMENT_LIMITS.maxCodeChars),
  replacement: z.string().max(IMPROVEMENT_LIMITS.maxCodeChars),
});
export type ImprovementSuggestion = z.infer<typeof ImprovementSuggestion>;

export interface AiImproveResponse {
  suggestions: ImprovementSuggestion[];
  model: string;
}

/** Linhas startLine..endLine (1-based, inclusivo) de um texto. */
export function sliceLines(text: string, startLine: number, endLine: number): string {
  return text
    .split("\n")
    .slice(startLine - 1, endLine)
    .join("\n");
}

/** Comparação tolerante a espaços no fim das linhas e a CRLF. */
export function sameCode(a: string, b: string): boolean {
  const norm = (s: string) =>
    s
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((l) => l.trimEnd())
      .join("\n")
      .trim();
  return norm(a) === norm(b);
}
